<template>
    <header class="flex h-16 shrink-0 items-center gap-2 border-b border-default px-3 sm:px-4">
        <button
            class="hover-animation cursor-pointer"
            :aria-label="$t('navigation.menu')"
            @click="emit('toggle-menu')"
        >
            <AppIcon name="bars" class="size-9" />
        </button>

        <div class="ml-auto flex items-center gap-2">
            <SwitchManager />

            <UButton
                color="neutral"
                variant="ghost"
                :label="locale === 'sl' ? 'EN' : 'SL'"
                @click="toggleLocale"
            />

            <UDropdownMenu :items="userItems">
                <button
                    class="hover-animation flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 sm:max-w-96 sm:border sm:border-default sm:bg-default sm:shadow-xs"
                >
                    <AppIcon name="user" class="size-9" />
                    <span class="hidden min-w-0 text-left font-semibold sm:block">{{ userLabel }}</span>
                    <UIcon name="i-lucide-chevron-down" class="ml-1 hidden size-4 shrink-0 stroke-[3] text-primary sm:block" />
                </button>
            </UDropdownMenu>
        </div>
    </header>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import AppIcon from '@/components/AppIcon.vue'
    import SwitchManager from '@/layouts/components/SwitchManager.vue'
    import { parseError } from '@/libs/error'
    import { PrintError } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch.ts'

    const emit = defineEmits<(e: 'toggle-menu') => void>()

    // data
    const { t, locale } = useI18n()
    const appStore = useAppStore()
    const fetchStore = useFetchStore()
    const router = useRouter()

    // computed
    const userData = computed(() => {
        return appStore.getUserData()
    })

    const userLabel = computed(() => {
        return `${userData.value.firstname} ${userData.value.lastname}`.trim()
    })

    // methods
    const toggleLocale = () => {
        const next = locale.value === 'sl' ? 'en' : 'sl'
        locale.value = next
        localStorage.setItem('locale', next)
    }

    const logout = async() => {
        const { response, data } = await fetchStore.fetch(`/logout`).get().json()
        if (response.value?.status === 200) {
            appStore.setLogout()
            await router.push({ name: 'login' })
        } else {
            PrintError(parseError(data.value, t('errors.on_logout')))
        }
    }

    const userItems = computed(() => [[
        {
            label: t('navigation.change_password'),
            icon: 'i-lucide-key-round',
            to: { name: 'change-password' }
        }
    ], [
        {
            label: t('navigation.logout'),
            icon: 'i-lucide-log-out',
            color: 'error' as const,
            onSelect: logout
        }
    ]])
</script>
